import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { FiX } from 'react-icons/fi';
import IonRangeSlider from 'react-ion-slider';
import 'ion-rangeslider/css/ion.rangeSlider.min.css';
import { enGB, sr, srLatn } from 'react-day-picker/locale';
import 'react-day-picker/style.css';
import { validateCustomRange, wallInputToMs, CUSTOM_MAX_MS } from '../../lib/charts/rangePresets';
import { toStationWall } from '../../lib/charts/time';
import { RANGE_SHORTCUTS } from '../../lib/charts/rangeShortcuts';

const LOCALES = { en: enGB, sr: srLatn, src: sr };
const prettifyHour = (v) => `${String(v).padStart(2, '0')}:00`;

/**
 * react-ion-slider calls the plugin's update() on EVERY React re-render, which
 * resets an in-progress drag. Memoised with stable props so it only re-renders
 * when the committed hours change (chips, open, finish) — never mid-drag.
 */
const HourSlider = React.memo(function HourSlider({ from, to, onLive, onFinish }) {
    return (
        <IonRangeSlider
            type="double" min={0} max={24} step={1} min_interval={1}
            from={from} to={to}
            grid grid_num={6} hide_min_max hide_from_to skin="round"
            prettify={prettifyHour}
            onChange={onLive}
            onFinish={onFinish}
        />
    );
});
const FLYING_HOURS = [9, 19];
const WHOLE_DAY = [0, 24];
const MAX_DAYS = CUSTOM_MAX_MS / 86400e3; // 5 calendar days inclusive → 4 nights for DayPicker's `max`

// Calendar days are handled as station (Belgrade) dates: a picked Date's local
// Y-M-D is the wall date; hours are added in wall time and converted to epoch.
const pad = (n) => String(n).padStart(2, '0');
const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const wallToDate = (wall) => { const [y, m, d] = wall.split('-').map(Number); return new Date(y, m - 1, d); };
const msToDate = (ms) => wallToDate(toStationWall(ms, 'YYYY-MM-DD'));
const msToHour = (ms) => Number(toStationWall(ms, 'H'));

function boundsToState(fromMs, toMs) {
    const from = msToDate(fromMs);
    const toHourRaw = msToHour(toMs);
    const toMidnight = toHourRaw === 0 && Number(toStationWall(toMs, 'm')) === 0;
    // 00:00 on the day after == "24:00" of the previous day for the picker.
    const to = toMidnight ? msToDate(toMs - 1) : msToDate(toMs);
    return { range: { from, to }, fromHour: msToHour(fromMs), toHour: toMidnight ? 24 : toHourRaw };
}

function stateToBounds(range, fromHour, toHour) {
    if (!range || !range.from) return null;
    const to = range.to || range.from;
    const fromMs = wallInputToMs(`${dateKey(range.from)}T${pad(fromHour)}:00`);
    let toMs;
    if (toHour === 24) {
        const next = new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1);
        toMs = wallInputToMs(`${dateKey(next)}T00:00`);
    } else {
        toMs = wallInputToMs(`${dateKey(to)}T${pad(toHour)}:00`);
    }
    return { fromMs, toMs };
}

/**
 * Custom range picker: shortcut chips, a range calendar (react-day-picker)
 * and an hour range slider, all in station time. Desktop: centred dialog
 * anchored to the viewport (never off-screen); mobile (`compact`): inline panel.
 */
const CustomRangePicker = ({ open, nowMs, initialFromMs, initialToMs, onApply, onCancel, labels, compact = false, historySince, locale = 'en' }) => {
    const monthsShown = compact ? 1 : 2;
    // Two months on desktop: the anchor date's month on the RIGHT, the previous month on the left.
    const firstMonthFor = (date) => new Date(date.getFullYear(), date.getMonth() - (monthsShown - 1), 1);
    const [state, setState] = useState(() => boundsToState(initialFromMs, initialToMs));
    const [error, setError] = useState(null);
    const readoutRef = useRef(null);
    // The displayed month is CONTROLLED and only moves on open, on a shortcut, or by the arrows —
    // never as a side effect of clicking a day, or a two-click range selection would land on a
    // re-rendered calendar.
    const [month, setMonth] = useState(() => firstMonthFor(boundsToState(initialFromMs, initialToMs).range.from));

    useEffect(() => {
        if (open) {
            const next = boundsToState(initialFromMs, initialToMs);
            setState(next);
            setMonth(firstMonthFor(next.range.from));
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialFromMs, initialToMs]);

    const today = useMemo(() => msToDate(nowMs), [nowMs]);
    const startMonth = useMemo(() => (historySince ? wallToDate(`${historySince}-01`) : undefined), [historySince]);
    const disabled = useMemo(() => {
        const rules = [{ after: today }];
        if (historySince) rules.push({ before: wallToDate(`${historySince}-01`) });
        return rules;
    }, [today, historySince]);

    // During a drag only the readout text changes (no React state → no plugin reset).
    const onSliderLive = useCallback((e) => { if (readoutRef.current) readoutRef.current.textContent = `${pad(e.from)}:00 – ${pad(e.to)}:00`; }, []);
    const onSliderFinish = useCallback((e) => { setState((st) => (st.fromHour === e.from && st.toHour === e.to ? st : { ...st, fromHour: e.from, toHour: e.to })); setError(null); }, []);

    // Desktop dialog: Escape closes.
    useEffect(() => {
        if (!open || compact) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, compact, onCancel]);

    if (!open) return null;

    const apply = () => {
        const bounds = stateToBounds(state.range, state.fromHour, state.toHour);
        if (!bounds || Number.isNaN(bounds.fromMs) || Number.isNaN(bounds.toMs)) { setError(labels.errors.noRange); return; }
        // "Whole day" on today naturally ends after now: clamp to now rather than reject.
        if (bounds.toMs > nowMs && bounds.fromMs < nowMs) bounds.toMs = nowMs;
        const v = validateCustomRange(bounds.fromMs, bounds.toMs, nowMs);
        if (!v.ok) { setError(labels.errors[v.reason]); return; }
        setError(null);
        onApply(bounds.fromMs, bounds.toMs);
    };
    const pick = (shortcut) => {
        const r = shortcut.compute(nowMs);
        const next = boundsToState(r.fromMs, r.toMs);
        setState(next);
        setMonth(firstMonthFor(next.range.from));
        setError(null);
    };
    const setHours = ([f, t]) => { setState((st) => ({ ...st, fromHour: f, toHour: t })); setError(null); };
    const isWholeDay = state.fromHour === WHOLE_DAY[0] && state.toHour === WHOLE_DAY[1];
    const isFlying = state.fromHour === FLYING_HOURS[0] && state.toHour === FLYING_HOURS[1];
    const chip = 'rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 min-h-[32px]';

    const panel = (
        <div role="dialog" aria-labelledby="custom-range-title"
             className={`${compact ? 'mt-2 w-full rdp-compact' : 'fixed z-[1200] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,680px)] max-h-[90vh] overflow-y-auto'} rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-2xl p-3 flex flex-col gap-3`}>
            <div className="flex items-center justify-between gap-2">
                <h3 id="custom-range-title" className="text-base font-bold tracking-tight uppercase text-zinc-700 dark:text-white">{labels.title}</h3>
                {compact ? null : (
                    <button type="button" onClick={onCancel} aria-label={labels.close || 'Close'}
                            className="-mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white">
                        <FiX className="h-5 w-5" />
                    </button>
                )}
            </div>
            <div className="flex flex-wrap gap-2">
                {RANGE_SHORTCUTS.map((s) => (
                    <button key={s.key} type="button" className={chip} onClick={() => pick(s)}>{labels.shortcuts[s.key]}</button>
                ))}
            </div>
            <DayPicker
                mode="range"
                selected={state.range}
                onSelect={(range) => { setState((s) => ({ ...s, range: range || { from: undefined, to: undefined } })); setError(null); }}
                max={MAX_DAYS - 1}
                numberOfMonths={monthsShown}
                month={month}
                onMonthChange={setMonth}
                startMonth={startMonth}
                endMonth={today}
                disabled={disabled}
                locale={LOCALES[locale] || enGB}
                weekStartsOn={1}
            />
            <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs text-slate-600 dark:text-slate-300">
                        {labels.hours}
                        <span ref={readoutRef} className="ml-2 text-sm font-semibold text-slate-800 dark:text-white">{`${pad(state.fromHour)}:00 – ${pad(state.toHour)}:00`}</span>
                        {isWholeDay ? <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">· {labels.wholeDay}</span> : null}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" className={`${chip} ${isWholeDay ? 'bg-slate-200 dark:bg-slate-600' : ''}`} onClick={() => setHours(WHOLE_DAY)}>{labels.wholeDay}</button>
                        <button type="button" className={`${chip} ${isFlying ? 'bg-slate-200 dark:bg-slate-600' : ''}`} onClick={() => setHours(FLYING_HOURS)}>{labels.flyingHours} 09–19</button>
                    </div>
                </div>
                <div className="px-1">
                    <HourSlider from={state.fromHour} to={state.toHour} onLive={onSliderLive} onFinish={onSliderFinish} />
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-slate-500 dark:text-slate-400">{labels.hint}{labels.archive ? ` · ${labels.archive}` : ''}</span>
                <div className="flex gap-2 ml-auto">
                    <button type="button" onClick={apply} className="min-h-[44px] md:min-h-[32px] rounded-md bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700">{labels.apply}</button>
                    <button type="button" onClick={onCancel} className="min-h-[44px] md:min-h-[32px] rounded-md border border-slate-300 dark:border-slate-600 px-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">{labels.cancel}</button>
                </div>
                {error ? <p role="alert" className="basis-full text-xs text-red-600 dark:text-red-400">{error}</p> : null}
            </div>
        </div>
    );

    if (compact) return panel;
    return (
        <>
            <div className="fixed inset-0 z-[1190] bg-slate-900/40 backdrop-blur-[1px]" onClick={onCancel} aria-hidden="true" />
            {panel}
        </>
    );
};

export default CustomRangePicker;
